import { useCallback, useEffect, useMemo } from 'react';
import {
  ApolloError,
  makeVar,
  MutationHookOptions,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar
} from '@apollo/client';
import {
  CacheableActiveRequestStatus,
  IBlockPackageUpdateResponse,
  IRequest,
  IRequestBlock,
  IRequestOverview,
  Identifiable,
  REQUEST_NOTIFY_OPTION,
  REQUEST_STATUS
} from '../../../lib/types';
import {
  CREATE_REQUEST,
  DELETE_REQUEST,
  GET_REQUEST,
  GET_REQUEST_WITH_TOKEN,
  LIST_REQUESTS_AS_STAFF,
  RESTORE_REQUEST,
  SEND_REQUEST,
  UPDATE_REQUEST_WITH_TOKEN,
  UPDATE_REQUEST,
  UPDATE_REQUEST_AS_CLIENT,
  CLEAR_REQUEST,
  DUPLICATE_REQUEST,
  SEND_CUSTOM_REQUEST_MESSAGE,
  SEND_CUSTOM_REQUEST_MESSAGE_WITH_TOKEN,
  CREATE_REQUEST_FROM_TEMPLATE,
  UPDATE_REQUEST_ASSIGNMENT,
  UNSEND_REQUEST
} from './request.queries';
import {
  ICreateRequestCallParams,
  IListRequestAsStaffResults,
  IListRequestAsStaffVariables,
  IRequestByIdResults,
  IRequestWithTokenResults,
  ISendCustomRequestMessageParams,
  ISendRequestParams,
  IUpdateRequestCallParams
} from './request.types';
import toast from 'react-hot-toast';
import { showError } from '../../../lib/utils';
import {
  fillInRequestsCache,
  readRequestsCache,
  updateRequestsCacheAfterModification,
  writeRequestsCache
} from './request.utils';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { DEFAULT_LIMIT } from './request.consts';
import { IPermanent } from '../request-group/request-group.types';
import { GET_REQUEST_GROUP, GET_REQUEST_GROUPS } from '../request-group/request-group.queries';

export const requestPollingPausedVar = makeVar<boolean>(false);

interface IRequestWithTokenParams {
  ignoreStatus?: boolean;
  requestId: string;
  token: string;
}

const onUpdateRequestError = (error: ApolloError, action?: string) => {
  if (error.message.includes('One or more contact IDs do not have access to this request')) {
    toast.error('One or more contacts do not have access to this request');
  } else if (action) showError(`Failed to ${action} request`, error);
};

export const useClearRequest = ({ _id }: Identifiable, afterClear?: () => void) => {
  const [mutation, rest] = useMutation<{ clearRequest: IRequest }>(CLEAR_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.clearRequest) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.clearRequest } as IRequestByIdResults,
          variables: { _id },
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.clearRequest] });

        afterClear?.();
      }
    },
    onCompleted: () => toast.success('Request cleared successfully!'),
    onError: (error) => onUpdateRequestError(error, 'clear')
  });

  const clearRequest = useCallback(() => mutation({ variables: { _id } }), [_id, mutation]);

  return { clearRequest, ...rest };
};

export const useDuplicateRequest = ({ _id }: Identifiable) => {
  const [mutation, rest] = useMutation<{ duplicateRequest: IRequest }>(DUPLICATE_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.duplicateRequest) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.duplicateRequest } as IRequestByIdResults,
          variables: { _id: data.duplicateRequest._id },
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.duplicateRequest] });
      }
    },
    onCompleted: () => toast.success('Request duplicated successfully!'),
    onError: (error) => {
      onUpdateRequestError(error, 'duplicate');
      throw error;
    }
  });

  const duplicateRequest = useCallback((clear: boolean) => mutation({ variables: { _id, clear } }), [_id, mutation]);

  return { duplicateRequest, ...rest };
};

export const useRequestWithToken = (variables: IRequestWithTokenParams) => {
  const skip = useReactiveVar(requestPollingPausedVar);

  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IRequestWithTokenResults, IRequestWithTokenParams>(GET_REQUEST_WITH_TOKEN, {
    variables,
    skip
  });

  const requestResponse = useMemo(() => data?.requestByWithToken, [data?.requestByWithToken]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    requestResponse
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, requestResponse]);
};

export const useRequestWithAuth = (variables: Identifiable) => {
  const skip = useReactiveVar(requestPollingPausedVar);

  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IRequestByIdResults, Identifiable>(GET_REQUEST, {
    fetchPolicy: 'cache-and-network',
    variables,
    skip
  });

  const request = useMemo(() => data?.requestById, [data?.requestById]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    request
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, request]);
};

export const useLazyRequest = () => {
  const [query, rest] = useLazyQuery<IRequestByIdResults, Identifiable>(GET_REQUEST, {
    fetchPolicy: 'cache-and-network'
  });

  const getRequest = useCallback((variables: Identifiable) => query({ variables }), [query]);
  return { getRequest, ...rest };
};

export const useRequests = ({
  includeArchived = false,
  limit = DEFAULT_LIMIT,
  page = 1,
  status: unsortedStatus,
  ...restVars
}: IListRequestAsStaffVariables = {}) => {
  const status = useMemo(
    () =>
      unsortedStatus ? unsortedStatus.sort().reduce((acc: REQUEST_STATUS[], curr) => [...acc, curr], []) : undefined,
    [unsortedStatus]
  );

  const client = useApolloClient();

  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, startPolling, stopPolling, subscribeToMore, updateQuery, previousData } = useQuery<IListRequestAsStaffResults, IListRequestAsStaffVariables>(LIST_REQUESTS_AS_STAFF, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60 * 1000,
    variables: { ...restVars, status, includeArchived, page, limit }
  });

  useEffect(() => {
    if (data?.listRequestsAsStaff && !loading && called) {
      const isDraft = status?.includes(REQUEST_STATUS.DRAFT);

      // Fill alternate includeArchived results
      if (status) {
        const alternateIncludeArchived = !includeArchived;
        const alternateRequests = alternateIncludeArchived
          ? data.listRequestsAsStaff.requests
          : data.listRequestsAsStaff.requests.filter((r) => !r.deletedAt);

        writeRequestsCache({
          ...restVars,
          status,
          cache: client.cache,
          includeArchived: alternateIncludeArchived,
          listRequestsAsStaff: {
            includeArchived: alternateIncludeArchived,
            page,
            status,
            requests: alternateRequests,
            total: alternateRequests.length
          },
          page,
          limit
        });
      }

      // If other variants for request type, then fill in those with available data
      if ((!status || !isDraft) && data?.listRequestsAsStaff && !loading && called) {
        fillInRequestsCache({
          ...restVars,
          status: status as CacheableActiveRequestStatus[],
          cache: client.cache,
          page,
          limit,
          includeArchived,
          requests: data.listRequestsAsStaff.requests
        });
      }
    }
  }, [client.cache, data, includeArchived, limit, page, called, loading, restVars, status]);

  const requests = useMemo(() => data?.listRequestsAsStaff?.requests, [data?.listRequestsAsStaff?.requests]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    startPolling,
    stopPolling,
    subscribeToMore,
    updateQuery,
    previousData,
    requests
  }), [combinedLoading, error, called, refetch, networkStatus, startPolling, stopPolling, subscribeToMore, updateQuery, previousData, requests]);
};

const useCreateBaseRequest = () => {
  const [mutation, rest] = useMutation<{ createRequest: IRequest }>(CREATE_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.createRequest) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.createRequest } as IRequestByIdResults,
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.createRequest] });
      }
    },
    onCompleted: () => toast.success('Request created successfully!'),
    onError: (error) => {
      if (error.message.includes('One or more contact IDs do not have access to this request')) {
        toast.error('One or more contacts do not have access to this request');
      }
    }
  });

  const createRequest = useCallback((variables: ICreateRequestCallParams) => mutation({ variables }), [mutation]);

  return { createRequest, ...rest };
};

const useCreateRequestFromTemplate = () => {
  const [mutation, rest] = useMutation<{ createRequestFromTemplate: IRequest }>(CREATE_REQUEST_FROM_TEMPLATE, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.createRequestFromTemplate) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.createRequestFromTemplate } as IRequestByIdResults,
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.createRequestFromTemplate] });
      }
    },
    onCompleted: () => toast.success('Request created successfully!'),
    onError: (error) => {
      if (error.message.includes('One or more contact IDs do not have access to this request')) {
        toast.error('One or more contacts do not have access to this request');
      }
    }
  });

  const createRequestFromTemplate = useCallback(
    (variables: ICreateRequestCallParams) => mutation({ variables }),
    [mutation]
  );

  return { createRequestFromTemplate, ...rest };
};

export const useCreateRequest = () => {
  const { createRequest: createBaseRequest, loading: creatingBaseRequest } = useCreateBaseRequest();
  const { createRequestFromTemplate, loading: creatingRequestFromTemplate } = useCreateRequestFromTemplate();

  const createRequest = useCallback(
    async (variables: ICreateRequestCallParams) => {
      let request: IRequest | null;
      if (variables.templateId)
        request = (await createRequestFromTemplate(variables)).data?.createRequestFromTemplate ?? null;
      else request = (await createBaseRequest(variables)).data?.createRequest ?? null;

      return request;
    },
    [createBaseRequest, createRequestFromTemplate]
  );

  return { createRequest, loading: creatingBaseRequest || creatingRequestFromTemplate };
};

export const useSendRequest = () => {
  const [mutation, rest] = useMutation<{ sendRequest: IRequest }, ISendRequestParams>(SEND_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data?.sendRequest) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.sendRequest } as IRequestByIdResults,
          variables: { _id: variables?._id },
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.sendRequest] });
      }
    },
    onCompleted: () => toast.success('Request sent successfully!'),
    onError: (error) => {
      const errMsg = error.message.toLowerCase();
      if (errMsg.includes('blocks are required')) toast.error('Request must have content');
      else if (errMsg.includes('the request must be assigned to a contact'))
        toast.error('Request must be assigned to a contact');
      else showError('Failed to send request', error);
    }
  });

  const sendRequest = useCallback((variables: ISendRequestParams) => mutation({ variables }), [mutation]);

  return { sendRequest, ...rest };
};

export const useUpdateRequest = ({ _id, lastUpdatedAt }: Identifiable & { lastUpdatedAt?: Date }) => {
  const [mutation, rest] = useMutation<{ updateRequest: IBlockPackageUpdateResponse }>(UPDATE_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.updateRequest.request) {
        if (data.updateRequest.error)
          toast.error(`Failed to update request. ${data.updateRequest.error}.`, { id: 'request-update-' + _id });

        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.updateRequest.request } as IRequestByIdResults,
          variables: { _id },
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.updateRequest.request] });
      } else showError('Failed to update request', new Error(data?.updateRequest.error ?? 'Unknown error'));
    },
    onError: (error) => onUpdateRequestError(error)
  });

  const updateRequest = useCallback(
    async (variables: IUpdateRequestCallParams, overrideTimestamp?: Date) => {
      const timestampToUse = overrideTimestamp ?? lastUpdatedAt;
      const result = await mutation({ variables: { ...variables, _id, lastUpdatedAt: timestampToUse } });
      return result.data?.updateRequest;
    },
    [_id, lastUpdatedAt, mutation]
  );

  return { updateRequest, ...rest };
};

export const useUpdateRequestAsClient = (variables: IRequestWithTokenParams & { lastUpdatedAt: Date }) => {
  const [asClientMutation, asClientRest] = useMutation<{ updateRequestAsClient: IBlockPackageUpdateResponse }>(
    UPDATE_REQUEST_AS_CLIENT,
    {
      context: { serializationKey: 'MUTATION', tracked: true },
      update: (cache, { data }) => {
        if (data?.updateRequestAsClient.request) {
          cache.writeQuery({
            query: GET_REQUEST_WITH_TOKEN,
            data: { requestByWithToken: { request: data.updateRequestAsClient.request } } as IRequestWithTokenResults,
            variables,
            overwrite: true
          });
        }
      }
    }
  );

  const [withTokenMutation, withTokenRest] = useMutation<{ updateRequestWithToken: IBlockPackageUpdateResponse }>(
    UPDATE_REQUEST_WITH_TOKEN,
    {
      context: { serializationKey: 'MUTATION', tracked: true },
      update: (cache, { data }) => {
        if (data?.updateRequestWithToken.request) {
          cache.writeQuery({
            query: GET_REQUEST_WITH_TOKEN,
            data: { requestByWithToken: { request: data.updateRequestWithToken.request } } as IRequestWithTokenResults,
            variables,
            overwrite: true
          });
        }
      }
    }
  );

  const updateRequestAsClient = useCallback(
    async (blocks: IRequestBlock[], overrideTimestamp?: Date) => {
      const timestampToUse = overrideTimestamp ?? variables.lastUpdatedAt;
      const mutationData: { variables: { blocks: IRequestBlock[]; _id: string; lastUpdatedAt: Date; token?: string } } =
        { variables: { blocks, _id: variables.requestId, lastUpdatedAt: timestampToUse } };

      if (variables.token) {
        mutationData.variables.token = variables.token;
        return (await withTokenMutation(mutationData)).data?.updateRequestWithToken;
      }
      return (await asClientMutation(mutationData)).data?.updateRequestAsClient;
    },
    [asClientMutation, variables.lastUpdatedAt, variables.requestId, variables.token, withTokenMutation]
  );

  return { updateRequestAsClient, ...(variables.token ? withTokenRest : asClientRest) };
};

export const useUpdateRequestAssignment = () => {
  const [mutation, rest] = useMutation<{ updateRequestAssignment: IRequestOverview }, Identifiable>(
    UPDATE_REQUEST_ASSIGNMENT,
    {
      context: { serializationKey: 'MUTATION', tracked: true },
      update: (cache, { data }) => {
        if (data)
          updateRequestsCacheAfterModification({
            cache,
            remove: !data.updateRequestAssignment.currStaffIsAssigned,
            updated: [data.updateRequestAssignment]
          });
      }
    }
  );

  const updateRequestAssignment = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { updateRequestAssignment, ...rest };
};

export const useDeleteRequest = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ deleteRequest: IRequest }, Identifiable & IPermanent>(DELETE_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data) {
        updateRequestsCacheAfterModification({
          cache,
          remove: variables?.permanent,
          updated: [data.deleteRequest]
        });

        if (variables?.permanent)
          client.refetchQueries({
            include: [GET_REQUEST_GROUPS, GET_REQUEST_GROUP, GET_REQUEST, LIST_REQUESTS_AS_STAFF]
          });
      }
    }
  });

  const deleteRequest = useCallback((variables: Identifiable & IPermanent) => mutation({ variables }), [mutation]);

  return { deleteRequest, ...rest };
};

export const useRestoreRequest = () => {
  const [mutation, rest] = useMutation<{ restoreRequest: IRequest }, Identifiable>(RESTORE_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateRequestsCacheAfterModification({ cache, updated: [data.restoreRequest] });
    }
  });

  const restoreRequest = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreRequest, ...rest };
};

export const useSendCustomRequestMessage = ({
  isResponse,
  requestId,
  token,
  ignoreStatus
}: IRequestWithTokenParams & { isResponse?: boolean }) => {
  const mutationOptions: MutationHookOptions = {
    context: { serializationKey: 'MUTATION', tracked: true },
    onCompleted: (data: any) => {
      const response = data?.sendCustomRequestMessageWithToken || data?.sendCustomRequestMessage;
      if (response?.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        isResponse ? toast.success('Submitted request succesfully!') : toast.success('Message sent successfully!');
      }
    },
    onError: (error: Error) => showError('Failed to send message', error),
    update: (cache) => {
      if (isResponse) {
        // Update request to mark as client finished working
        const listRequestsAsStaff = readRequestsCache({ cache });
        const currRequestOverview = listRequestsAsStaff?.requests.find((r) => r._id === requestId);
        if (currRequestOverview)
          updateRequestsCacheAfterModification({
            cache,
            updated: [{ ...currRequestOverview, clientFinishedWorking: true }]
          });

        if (token) {
          const currRequestDetails = cache.readQuery<IRequestWithTokenResults, IRequestWithTokenParams>({
            query: GET_REQUEST_WITH_TOKEN,
            variables: { requestId, token, ignoreStatus }
          })?.requestByWithToken;

          if (currRequestDetails?.request)
            cache.writeQuery<IRequestWithTokenResults, IRequestWithTokenParams>({
              query: GET_REQUEST_WITH_TOKEN,
              data: { requestByWithToken: { request: { ...currRequestDetails.request, clientFinishedWorking: true } } },
              variables: { requestId, token, ignoreStatus },
              overwrite: true
            });
        } else {
          const currRequestDetails = cache.readQuery<IRequestByIdResults, Identifiable>({
            query: GET_REQUEST,
            variables: { _id: requestId }
          })?.requestById;

          if (currRequestDetails)
            cache.writeQuery<IRequestByIdResults, Identifiable>({
              query: GET_REQUEST,
              data: { requestById: { ...currRequestDetails, clientFinishedWorking: true } },
              variables: { _id: requestId },
              overwrite: true
            });
        }
      }
    }
  };

  const [authenticatedMutation, asAuthenticatedRest] = useMutation<{ sendCustomRequestMessage: { success: boolean; redirectUrl?: string } }>(
    SEND_CUSTOM_REQUEST_MESSAGE,
    mutationOptions
  );

  const [withTokenMutation, withTokenRest] = useMutation<{ sendCustomRequestMessageWithToken: { success: boolean; redirectUrl?: string } }>(
    SEND_CUSTOM_REQUEST_MESSAGE_WITH_TOKEN,
    mutationOptions
  );

  const sendCustomRequestMessage = useCallback(
    ({ notify = [REQUEST_NOTIFY_OPTION.ALL], ...restParams }: ISendCustomRequestMessageParams) => {
      const mutationData: {
        variables: ISendCustomRequestMessageParams & Identifiable & { completed?: boolean; token?: string };
      } = {
        variables: { ...restParams, token, _id: requestId, notify }
      };

      if (token) {
        mutationData.variables.token = token;
        if (isResponse) mutationData.variables.completed = true;
        return withTokenMutation(mutationData);
      }
      return authenticatedMutation(mutationData);
    },
    [authenticatedMutation, isResponse, requestId, token, withTokenMutation]
  );

  return { sendCustomRequestMessage, ...(token ? withTokenRest : asAuthenticatedRest) };
};

export const useUnsendRequest = ({ _id }: Identifiable) => {
  const [mutation, rest] = useMutation<{ unsendRequest: IRequest }>(UNSEND_REQUEST, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.unsendRequest) {
        cache.writeQuery({
          query: GET_REQUEST,
          data: { requestById: data.unsendRequest } as IRequestByIdResults,
          variables: { _id },
          overwrite: true
        });

        updateRequestsCacheAfterModification({ cache, updated: [data.unsendRequest] });
      }
    },
    onCompleted: () => toast.success('Request unsent successfully!'),
    onError: (error) => onUpdateRequestError(error, 'unsend')
  });

  const unsendRequest = useCallback(() => mutation({ variables: { _id } }), [_id, mutation]);

  return { unsendRequest, ...rest };
};
